/**
 * Feeds the setup tracker (components/Welcome).
 *
 * Two modes:
 *
 * - **demo** (`/welcome?demo=1`) — no network. Walks the whole flow on a timer
 *   so the screen can be designed and reviewed end to end. `?stage=review`
 *   parks it on one stage instead.
 *
 * - **live** — reads `GET /api/setup/status` as the source of truth. If that
 *   endpoint is missing during rollout, it falls back to the older composed
 *   payload (`/api/account/trial-status`, `/api/websites`,
 *   `/api/websites/:id/deployment`, `/api/payment/subscription`).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import {
  SETUP_STAGES,
  STAGE_DEFS,
  buildMockStatus,
  stageIndex,
  type DomainStatus,
  type SetupEvent,
  type SetupStage,
  type SetupStatus,
  type SetupTask,
} from '../lib/setupFlow';
import { MONTHLY, PLAN_NAMES, type PlanId } from '../lib/pricing';
import { normalizePlan } from '../lib/planFeatures';
import { applyTaskStore, readTaskStore, type TaskStore } from '../lib/setupTasks';

/** How long each stage lingers in demo mode before it advances. */
const DEMO_STAGE_MS = 7000;
/** Live mode re-checks this often — the tracker should feel awake. */
const LIVE_POLL_MS = 20_000;

interface ApiWebsite {
  id: string;
  domain: string;
  currentActiveUrl?: string | null;
  netlifyUrl?: string | null;
  websiteStatus?: string | null;
  status?: string | null;
  screenshotUrl?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface ApiDeployment {
  id: string;
  status: string;
  detail?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

interface ApiTrialStatus {
  status: 'trial' | 'active' | 'expired';
  hasSubscription: boolean;
  trialEndsAt: string | null;
}

interface ApiSubscription {
  planType?: PlanId;
  amount?: number;
  status?: string;
  currentPeriodStart?: string;
}

/** Nothing on this screen is worth an unbounded wait. */
const REQUEST_TIMEOUT_MS = 12_000;

class ApiNotFoundError extends Error {}

interface LiveStatusResult {
  status: SetupStatus;
  source: 'setup' | 'fallback';
}

/**
 * One GET, never throwing and never hanging.
 *
 * The hang guard is not paranoia: the shared 401-refresh queue in `useAuth`
 * can drop a promise when several requests fail auth at the same instant, and
 * a dropped promise here would mean a spinner that never resolves. Calls are
 * also made one at a time (see `fetchLiveStatus`) so we never trigger that
 * race in the first place.
 */
async function get<T>(url: string): Promise<T | null> {
  try {
    const response = await Promise.race([
      axios.get(url, { timeout: REQUEST_TIMEOUT_MS }),
      new Promise<never>((_, reject) =>
        window.setTimeout(() => reject(new Error(`Timed out: ${url}`)), REQUEST_TIMEOUT_MS)
      ),
    ]);
    return (response.data?.data ?? response.data) as T;
  } catch {
    return null;
  }
}

async function getRequired<T>(url: string): Promise<T> {
  try {
    const response = await Promise.race([
      axios.get(url, { timeout: REQUEST_TIMEOUT_MS }),
      new Promise<never>((_, reject) =>
        window.setTimeout(() => reject(new Error(`Timed out: ${url}`)), REQUEST_TIMEOUT_MS)
      ),
    ]);
    return (response.data?.data ?? response.data) as T;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new ApiNotFoundError(url);
    }
    throw error;
  }
}

/**
 * Hosts we hand out ourselves. Kept in step with `FOUNDLIO_HOST_SUFFIXES` in
 * backend/src/services/setupService.ts — the two must agree on what counts as
 * "the client's own domain", because that is what "live" means.
 */
const FOUNDLIO_HOST_SUFFIXES = [
  'foundlio.com',
  'foundlio.site',
  'pages.dev',
  'workers.dev',
  'netlify.app',
  'vercel.app',
];

function bareHost(value: string | null | undefined): string {
  return (value ?? '')
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/[/?#].*$/, '')
    .replace(/^www\./, '');
}

/** The client's own domain, or null while the site is on an address we gave it. */
function customDomainOf(website: ApiWebsite | null): string | null {
  const host = bareHost(website?.domain);
  if (!host) return null;
  const ours = FOUNDLIO_HOST_SUFFIXES.some((suffix) => host === suffix || host.endsWith(`.${suffix}`));
  return ours ? null : host;
}

/**
 * Derive the stage from what the backend can actually tell us today.
 *
 * Deliberately conservative: "live" means live on a domain the client owns.
 * A site answering only on `<slug>.foundlio.com` is a preview, and claiming
 * otherwise is the one mistake this screen must not make.
 */
function deriveStage(
  trial: ApiTrialStatus | null,
  website: ApiWebsite | null,
  deployment: ApiDeployment | null
): SetupStage {
  if (!trial?.hasSubscription) return 'payment';
  if (!website) return 'payment';

  const websiteStatus = (website.websiteStatus ?? '').toLowerCase();
  if (customDomainOf(website)) return websiteStatus === 'live' ? 'live' : 'launch';

  const deployStatus = (deployment?.status ?? '').toLowerCase();
  if (['commit-created', 'queued', 'building'].includes(deployStatus)) return 'building';

  // No draft to look at yet — still ours.
  if (!website.currentActiveUrl && !website.netlifyUrl) return 'building';

  // A draft is up on the Foundlio address. This fallback has no view of the
  // SetupTask rows, so it parks here; the task store overlays completions.
  return 'review';
}

function deriveTasks(
  stage: SetupStage,
  website: ApiWebsite | null,
  domainStatus: DomainStatus
): SetupTask[] {
  const customDomain = customDomainOf(website);
  const hasDraft = Boolean(website?.currentActiveUrl || website?.netlifyUrl);

  return [
    {
      id: 'confirm-details',
      title: 'Confirm your business details',
      description: 'Hours, service area and the phone number calls should ring on.',
      owner: 'you',
      status: 'todo',
      cta: 'Confirm details',
      href: '/dashboard/account/profile',
    },
    {
      id: 'photos',
      title: 'Send us your photos',
      description: 'Real job photos beat stock every time. Five good ones is plenty.',
      owner: 'you',
      status: 'todo',
      cta: 'Upload photos',
      href: website ? `/dashboard/websites/${website.id}/edit` : '/dashboard/websites',
    },
    {
      id: 'review-draft',
      title: 'Review your draft site',
      description: 'Read it end to end and tell us anything that reads wrong.',
      owner: 'you',
      status: hasDraft ? 'todo' : 'doing',
      cta: 'Open preview',
      href: '#preview',
    },
    {
      id: 'domain',
      title: customDomain ? 'Your domain' : 'Connect or buy your domain',
      description: customDomain
        ? `${customDomain} is on file. We point it at your site and issue the certificate.`
        : 'Your site answers on a Foundlio address today. Point a name you already own at it, or we find and register one for you.',
      owner: 'you',
      status: domainStatus === 'connected' ? 'done' : 'todo',
      cta: 'Set up domain',
      href: '/dashboard/account/domain',
    },
    {
      id: 'gbp',
      title: 'Google Business Profile',
      description: 'We claim and optimise it so your site and your map listing agree.',
      owner: 'foundlio',
      status: stage === 'live' ? 'done' : 'doing',
    },
  ];
}

function deriveEvents(
  stage: SetupStage,
  website: ApiWebsite | null,
  deployment: ApiDeployment | null,
  subscription: ApiSubscription | null
): SetupEvent[] {
  const events: SetupEvent[] = [];

  if (subscription?.currentPeriodStart) {
    events.push({
      id: 'sub',
      at: subscription.currentPeriodStart,
      title: 'Payment confirmed',
      detail: subscription.planType
        ? `${PLAN_NAMES[normalizePlan(subscription.planType)]} plan`
        : undefined,
      actor: 'system',
    });
  }

  if (website?.createdAt) {
    events.push({
      id: 'site',
      at: website.createdAt,
      title: 'Project created',
      detail: website.domain,
      actor: 'system',
    });
  }

  if (deployment?.createdAt) {
    events.push({
      id: 'deploy-start',
      at: deployment.createdAt,
      title: 'Build started',
      actor: 'team',
    });
  }

  if (deployment?.updatedAt && deployment.status === 'ready') {
    events.push({
      id: 'deploy-ready',
      at: deployment.updatedAt,
      title: 'Draft ready for your review',
      detail: deployment.detail ?? undefined,
      actor: 'team',
    });
  }

  if (stage === 'live' && website?.updatedAt) {
    events.push({
      id: 'live',
      at: website.updatedAt,
      title: 'Your site is live',
      detail: website.domain,
      actor: 'system',
    });
  }

  return events.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
}

/**
 * Stand-in until the Website record carries the business name: strip the TLD
 * and any hyphens off the domain. `smith-plumbing.com` → `Smith Plumbing`.
 */
function businessNameFromDomain(domain: string | null | undefined): string {
  if (!domain) return 'Your business';
  const stem = domain.replace(/^www\./i, '').replace(/\.[a-z.]+$/i, '');
  return stem
    .split(/[-_.]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Rough ETA: ~22h per remaining stage. Replaced by a real promise date later. */
function deriveEta(stage: SetupStage): string | null {
  if (stage === 'live') return null;
  const remaining = SETUP_STAGES.length - 1 - stageIndex(stage);
  return new Date(Date.now() + remaining * 22 * 3_600_000).toISOString();
}

async function fetchLegacyLiveStatus(): Promise<SetupStatus> {
  // Sequential on purpose. A parallel burst that all 401s at once is what
  // trips the refresh-queue race in useAuth; one at a time, the first call
  // refreshes the token and the rest ride the new header.
  const trial = await get<ApiTrialStatus>('/api/account/trial-status');
  const websitesPayload = await get<{ websites: ApiWebsite[] }>('/api/websites');
  const subscription = await get<ApiSubscription>('/api/payment/subscription');

  // No trial status at all means the session is gone (or the API is down) —
  // better an honest error card than a tracker built out of nothing.
  if (!trial) throw new Error('Could not load account status');

  const website = websitesPayload?.websites?.[0] ?? null;
  const deploymentPayload = website
    ? await get<{ deployment: ApiDeployment | null }>(`/api/websites/${website.id}/deployment`)
    : null;
  const deployment = deploymentPayload?.deployment ?? null;

  const stage = deriveStage(trial, website, deployment);
  const plan: PlanId = normalizePlan(subscription?.planType);
  const previewUrl = website?.currentActiveUrl || website?.netlifyUrl || null;
  const customDomain = customDomainOf(website);
  const domainStatus: DomainStatus = customDomain ? 'connected' : 'none';

  return {
    stage,
    stageStartedAt: deployment?.updatedAt ?? website?.updatedAt ?? new Date().toISOString(),
    etaAt: deriveEta(stage),
    orderRef: website ? `FDL-${website.id.slice(-4).toUpperCase()}` : 'FDL-----',
    businessName: businessNameFromDomain(website?.domain),
    websiteId: website?.id ?? null,
    plan,
    monthly: subscription?.amount ? Math.round(subscription.amount / 100) : MONTHLY[plan],
    domain: website?.domain ?? null,
    customDomain,
    domainStatus,
    previewUrl,
    screenshotUrl: website?.screenshotUrl ?? null,
    // "Going live" has nothing ticked before the domain is connected.
    completedSteps:
      stage === 'live'
        ? STAGE_DEFS.live.steps.length
        : stage === 'launch'
          ? Number(Boolean(customDomain))
          : Math.max(1, STAGE_DEFS[stage].steps.length - 1),
    tasks: deriveTasks(stage, website, domainStatus),
    events: deriveEvents(stage, website, deployment, subscription),
    isMock: false,
  };
}

async function fetchLiveStatus(): Promise<LiveStatusResult> {
  try {
    return {
      status: await getRequired<SetupStatus>('/api/setup/status'),
      source: 'setup',
    };
  } catch (error) {
    if (error instanceof ApiNotFoundError) {
      return {
        status: await fetchLegacyLiveStatus(),
        source: 'fallback',
      };
    }
    throw error;
  }
}

export interface UseSetupStatusOptions {
  /** No network, fabricated payload, auto-advancing stages. */
  demo?: boolean;
  /** Park on one stage (both modes). */
  stageOverride?: SetupStage | null;
  /**
   * Hold the first fetch until the caller says the session is ready. Firing
   * while `useAuth` is still bootstrapping means racing it for the access
   * token: our requests 401, and the client's first impression of the screen
   * is an error card.
   */
  enabled?: boolean;
}

export interface UseSetupStatusResult {
  status: SetupStatus | null;
  isLoading: boolean;
  isError: boolean;
  /** Last time the payload actually changed hands — drives "updated 12s ago". */
  updatedAt: number;
  refresh: () => void;
  /** Demo only: jump the tracker to a stage. */
  setDemoStage: (stage: SetupStage) => void;
}

export function useSetupStatus({
  demo = false,
  stageOverride = null,
  enabled = true,
}: UseSetupStatusOptions = {}): UseSetupStatusResult {
  const [demoStage, setDemoStage] = useState<SetupStage>(stageOverride ?? 'payment');
  const [demoPaused, setDemoPaused] = useState(Boolean(stageOverride));

  // The optimistic mirror of what the client has ticked. Only demo and the
  // legacy fallback read it — with the real endpoint the server is the truth.
  // Kept in state (not read inline) so a completion re-renders immediately.
  const [taskStore, setTaskStore] = useState<TaskStore>(() => readTaskStore(demo));

  useEffect(() => {
    const sync = () => setTaskStore(readTaskStore(demo));
    sync();
    window.addEventListener('foundlio:setup-tasks-changed', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('foundlio:setup-tasks-changed', sync);
      window.removeEventListener('storage', sync);
    };
  }, [demo]);

  // Demo mode walks the flow so the whole thing can be seen in under a minute.
  useEffect(() => {
    if (!demo || demoPaused) return;
    const timer = window.setInterval(() => {
      setDemoStage((current) => {
        const next = SETUP_STAGES[stageIndex(current) + 1];
        return next ?? current;
      });
    }, DEMO_STAGE_MS);
    return () => window.clearInterval(timer);
  }, [demo, demoPaused]);

  const query = useQuery({
    queryKey: ['setup-status'],
    queryFn: fetchLiveStatus,
    enabled: !demo && enabled,
    refetchInterval: LIVE_POLL_MS,
    refetchOnWindowFocus: true,
    // No retry, and never gated on connectivity. A scheduled retry only runs
    // while the tab is focused (query-core `canContinue`), so a client who
    // tabs away after a failed first attempt comes back to a query stuck in
    // `paused` — neither loading nor errored, i.e. a spinner with no way out.
    // One attempt: it either resolves or shows an error card with a retry
    // button, and the 20s poll picks things up regardless.
    retry: false,
    networkMode: 'always',
  });

  useEffect(() => {
    if (demo) return;
    const refreshFromServer = () => {
      void query.refetch();
    };
    window.addEventListener('foundlio:setup-tasks-saved', refreshFromServer);
    return () => window.removeEventListener('foundlio:setup-tasks-saved', refreshFromServer);
  }, [demo, query.refetch]);

  const jumpDemoStage = useCallback((stage: SetupStage) => {
    setDemoPaused(true);
    setDemoStage(stage);
  }, []);

  const status = useMemo<SetupStatus | null>(() => {
    const base = demo
      ? buildMockStatus(demoStage)
      : query.data
        ? stageOverride
          ? { ...query.data.status, stage: stageOverride }
          : query.data.status
        : null;

    if (!base) return null;
    if (demo || query.data?.source === 'fallback') return applyTaskStore(base, taskStore);
    return base;
  }, [demo, demoStage, query.data, stageOverride, taskStore]);

  return {
    status,
    isLoading: !demo && query.isLoading,
    isError: !demo && query.isError,
    updatedAt: demo ? Date.now() : query.dataUpdatedAt,
    refresh: () => void query.refetch(),
    setDemoStage: jumpDemoStage,
  };
}
